import React from "react";
import { motion } from "framer-motion";

/**
 * Scroll-reveal wrapper powered by framer-motion. Fades + lifts children once
 * they enter the viewport. Composition-friendly: pass any content and tune
 * via `delay` / `as`.
 */
export const Reveal = ({ children, delay = 0, y = 32, className = "", as = "div" }) => {
  const MotionTag = motion[as] || motion.div;
  return (
    <MotionTag
      initial={{ opacity: 0, y, filter: "blur(6px)" }}
      whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
      viewport={{ once: true, margin: "-80px 0px -80px 0px" }}
      transition={{ duration: 0.95, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </MotionTag>
  );
};

/**
 * Masked line-by-line reveal: the children are split by <br/>-style lines
 * and each line slides up from under a clip-path mask. Signature on-load
 * moment for the hero.
 */
export const MaskedLines = ({ lines, className = "", lineClassName = "", delay = 0 }) => (
  <span className={className}>
    {lines.map((line, i) => (
      <span
        key={i}
        className="block overflow-hidden"
        style={{ paddingBottom: "0.08em" }}
      >
        <motion.span
          className={`block ${lineClassName}`}
          initial={{ y: "110%", opacity: 0.0001 }}
          animate={{ y: "0%", opacity: 1 }}
          transition={{
            duration: 1.1,
            delay: delay + i * 0.12,
            ease: [0.22, 1, 0.36, 1],
          }}
        >
          {line}
        </motion.span>
      </span>
    ))}
  </span>
);

/**
 * Simple character-stagger (used for small overlines).
 */
export const Chars = ({ text, className = "", delay = 0 }) => (
  <span className={className} aria-label={text}>
    {[...text].map((ch, i) => (
      <motion.span
        key={i}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: delay + i * 0.025, duration: 0.5, ease: "easeOut" }}
        style={{ display: "inline-block", whiteSpace: "pre" }}
      >
        {ch}
      </motion.span>
    ))}
  </span>
);
